import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, ShieldOff, Play, TrendingUp, Users } from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { api } from "@/lib/api";


export default function AdminEconomy() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const d = await api.adminEconomyDashboard();
            setData(d);
        } catch (e) {
            toast.error(e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const recompute = async () => {
        setBusy(true);
        try {
            const r = await api.adminEconomyRecompute();
            toast.success(`Recomputed. ${r.users_recomputed || 0} users updated.`);
            load();
        } catch (e) {
            toast.error(e.message || "Recompute failed");
        } finally {
            setBusy(false);
        }
    };

    const unsuspend = async (userId) => {
        const delta = Number(
            window.prompt(
                `Reputation delta to grant (default 50 — pushes score above 20).`,
                "50",
            ),
        );
        if (!Number.isFinite(delta) || delta <= 0) return;
        try {
            await api.adminEconomyUnsuspend({ user_id: userId, delta, reason: "manual_unsuspend" });
            toast.success("User unsuspended");
            load();
        } catch (e) {
            toast.error(e.message || "Unsuspend failed");
        }
    };

    return (
        <SidebarLayout>
            <div className="max-w-5xl mx-auto py-6 px-2">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <h1 className="text-2xl font-semibold">Economy dashboard</h1>
                        <p className="text-sm text-text-secondary">
                            Dynamic pricing engine, snapshot per-action, users sospesi.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <span
                            data-testid="economy-flag-pill"
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-widest ${
                                data?.flag_enabled
                                    ? "border-[var(--color-success)]/40 bg-[var(--color-success)]/10 text-[var(--color-success)]"
                                    : "border-[var(--color-border)] text-text-secondary"
                            }`}
                        >
                            <span
                                className="inline-block h-1.5 w-1.5 rounded-full"
                                style={{
                                    background: data?.flag_enabled
                                        ? "var(--color-success)"
                                        : "var(--color-text-secondary)",
                                }}
                            />
                            Multipliers {data?.flag_enabled ? "ENABLED" : "OFF"}
                        </span>
                        <button
                            data-testid="economy-recompute-btn"
                            onClick={recompute}
                            disabled={busy}
                            className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#EDEDED] px-4 py-2 text-xs text-black disabled:opacity-50"
                        >
                            <RefreshCw className="h-3 w-3" />
                            {busy ? "Running…" : "Recompute now"}
                        </button>
                        <button
                            onClick={load}
                            className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-3 py-2 text-xs text-text-secondary"
                        >
                            <RefreshCw className="h-3 w-3" /> Reload
                        </button>
                    </div>
                </div>

                {loading || !data ? (
                    <p className="text-sm text-text-secondary">Loading…</p>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <KPICard
                                icon={<Users className="h-4 w-4 text-[var(--color-primary)]" />}
                                label="Users with state"
                                value={data.total_users_with_state}
                            />
                            <KPICard
                                icon={<TrendingUp className="h-4 w-4 text-[var(--color-secondary)]" />}
                                label="Avg buy multiplier"
                                value={data.avg_reputation_buy_multiplier?.toFixed(3)}
                            />
                            <KPICard
                                icon={<ShieldOff className="h-4 w-4 text-[var(--color-alert)]" />}
                                label="Suspended"
                                value={data.suspended_users?.length}
                            />
                        </div>

                        <div className="mt-6" data-testid="economy-snapshots">
                            <h2 className="text-[10px] uppercase tracking-widest text-text-secondary">
                                Action snapshots (last)
                            </h2>
                            <div className="mt-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-[10px] uppercase tracking-widest text-text-secondary border-b border-[var(--color-border)]">
                                            <th className="px-4 py-3">Action</th>
                                            <th className="px-4 py-3">Demand</th>
                                            <th className="px-4 py-3">Sample</th>
                                            <th className="px-4 py-3">Base</th>
                                            <th className="px-4 py-3">Surge?</th>
                                            <th className="px-4 py-3">At</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.snapshots.map((s) => (
                                            <tr
                                                key={s.action_type}
                                                className="border-b border-[var(--color-border)] last:border-0"
                                            >
                                                <td className="px-4 py-3">{s.action_type}</td>
                                                <td className="px-4 py-3 tabular-nums">
                                                    {s.latest?.demand_factor?.toFixed(3) || "—"}
                                                </td>
                                                <td className="px-4 py-3 tabular-nums text-text-secondary">
                                                    {s.latest?.sample_count ?? "—"}
                                                </td>
                                                <td className="px-4 py-3 tabular-nums">
                                                    {s.latest?.base_credits_cost ?? "—"}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {s.latest?.is_surge ? (
                                                        <span className="text-[var(--color-alert)]">
                                                            ● surge
                                                        </span>
                                                    ) : (
                                                        <span className="text-text-secondary">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-[11px] text-text-secondary">
                                                    {s.latest?.timestamp
                                                        ? new Date(s.latest.timestamp).toLocaleString()
                                                        : "never"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="mt-8" data-testid="economy-suspended">
                            <h2 className="text-[10px] uppercase tracking-widest text-text-secondary">
                                Suspended users
                            </h2>
                            <div className="mt-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                                {data.suspended_users.length === 0 ? (
                                    <p className="p-6 text-sm text-text-secondary">
                                        No suspended users.
                                    </p>
                                ) : (
                                    <table className="min-w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[10px] uppercase tracking-widest text-text-secondary border-b border-[var(--color-border)]">
                                                <th className="px-4 py-3">User</th>
                                                <th className="px-4 py-3">Reason</th>
                                                <th className="px-4 py-3">Throttle</th>
                                                <th className="px-4 py-3"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.suspended_users.map((u) => (
                                                <tr
                                                    key={u.user_id}
                                                    data-testid={`suspended-row-${u.user_id}`}
                                                    className="border-b border-[var(--color-border)] last:border-0"
                                                >
                                                    <td className="px-4 py-3">
                                                        <p>{u.name || "?"}</p>
                                                        <p className="text-[11px] text-text-secondary">
                                                            {u.email}
                                                        </p>
                                                    </td>
                                                    <td className="px-4 py-3 text-xs text-text-secondary">
                                                        {u.suspension_reason || "—"}
                                                    </td>
                                                    <td className="px-4 py-3 tabular-nums">
                                                        {u.throttle_multiplier?.toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <button
                                                            data-testid={`unsuspend-${u.user_id}`}
                                                            onClick={() => unsuspend(u.user_id)}
                                                            className="inline-flex items-center gap-1 rounded-full bg-white hover:bg-[#EDEDED] px-3 py-1 text-[11px] text-black"
                                                        >
                                                            <Play className="h-3 w-3" /> Unsuspend
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        <p className="mt-8 text-[10px] text-text-secondary">
                            Feature flag <code>ECONOMY_MULTIPLIERS_ENABLED</code> è controllato via
                            backend `.env`. Non è modificabile da UI in Fase 4 per prevenire
                            impatti indesiderati sul UX. Passa a <code>true</code> e riavvia il
                            backend per attivarlo.
                        </p>
                    </>
                )}
            </div>
        </SidebarLayout>
    );
}

function KPICard({ icon, label, value }) {
    return (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary">
                {icon}
                {label}
            </div>
            <p className="mt-2 text-2xl font-semibold tabular-nums">{value ?? 0}</p>
        </div>
    );
}
